import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Stethoscope,
  HeartHandshake,
  ShieldCheck,
  Home,
  Building2,
  Clock,
  Calendar,
  BadgeCheck,
  MapPin,
  Mail,
  Phone,
  FileText,
  UserCheck,
  Award,
  Sparkles,
} from "lucide-react";
import {
  PageHero,
  GoldButton,
  GhostGoldButton,
  SectionEyebrow,
  FadedBackdrop,
  TrustChip,
} from "@/components/ui-bits";
import { IMG } from "@/constants/images";

// SEO structured data — Elder Care Notary as a dedicated Schema.org Service
// tied to OGO's LocalBusiness anchor. Optimizes for Brevard-County-specific
// long-tail queries like "bedside notary Cocoa Beach", "hospice notary
// Melbourne Florida", "ALF healthcare surrogate notary Brevard".
const ELDER_CARE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Elder Care & Senior Specialist Notary Services",
  provider: { "@id": "https://ogonotary.com/#business" },
  name: "Elder Care & Senior Specialist Notary Services — Brevard County, Florida",
  description:
    "Compassionate, dignified, and Chapter 117-compliant mobile notary services for seniors, hospice patients, assisted-living residents, and their families across Brevard County. Bedside notarization of Powers of Attorney, Healthcare Surrogate Designations, Living Wills, HIPAA Authorizations, and guardianship documents — delivered by a Certified Senior Advisor (CSA) Candidate and End-of-Life (EoL) Doula Candidate with 45 years of federal-grade operational discipline.",
  areaServed: [
    { "@type": "City", "name": "Cocoa Beach, FL" },
    { "@type": "City", "name": "Indialantic, FL" },
    { "@type": "City", "name": "Melbourne, FL" },
    { "@type": "City", "name": "Palm Bay, FL" },
    { "@type": "City", "name": "Rockledge, FL" },
    { "@type": "City", "name": "Merritt Island, FL" },
    { "@type": "City", "name": "Titusville, FL" },
    { "@type": "AdministrativeArea", "name": "Brevard County, Florida" },
  ],
  offers: [
    { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Bedside Notarization at Hospitals, Hospice, ALFs & Private Residences" } },
    { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Durable Power of Attorney Notarization" } },
    { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Healthcare Surrogate Designation Notarization" } },
    { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Living Will & Advance Directive Notarization" } },
    { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "HIPAA Authorization Notarization" } },
    { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Guardianship Documentation Notarization" } },
    { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "End-of-Life Planning Document Notarization" } },
  ],
};

const DOCUMENT_TYPES = [
  {
    icon: ShieldCheck,
    title: "Durable Powers of Attorney",
    body:
      "Financial and general powers of attorney executed under Florida Statute Chapter 709 — properly witnessed, notarized, and executed to survive incapacity.",
  },
  {
    icon: UserCheck,
    title: "Healthcare Surrogate Designations",
    body:
      "Designation of a trusted decision-maker per Florida Statute Chapter 765, including HIPAA authorization language for medical record access.",
  },
  {
    icon: FileText,
    title: "Living Wills & Advance Directives",
    body:
      "Advance directives, DNR-companion documentation, and end-of-life care preferences — notarized with the dignity and clarity the moment demands.",
  },
  {
    icon: HeartHandshake,
    title: "Guardianship & Conservatorship Documents",
    body:
      "Petitions, consents, and supporting affidavits for guardianship or conservatorship proceedings — coordinated with your elder-law attorney.",
  },
  {
    icon: Award,
    title: "Wills & Trust Documents",
    body:
      "Last wills, revocable living trusts, trust amendments, pour-over wills, and beneficiary certifications — often executed bedside during health transitions.",
  },
  {
    icon: Sparkles,
    title: "End-of-Life Planning Documents",
    body:
      "Funeral pre-arrangement authorizations, disposition instructions, and family-communication directives — held with the empathy of an EoL Doula Candidate.",
  },
];

const VENUES = [
  {
    icon: Building2,
    title: "Hospitals & Rehab Facilities",
    body:
      "Coordinated visits at Health First, Rockledge Regional, Palm Bay Hospital, Cape Canaveral Hospital, and every acute-care / rehab facility across Brevard County.",
  },
  {
    icon: HeartHandshake,
    title: "Hospice Locations & In-Home Hospice",
    body:
      "Compassionate hospice-setting notarizations at Vitas Healthcare, Hospice of St. Francis, William Childs Hospice House, and in-home hospice arrangements.",
  },
  {
    icon: Home,
    title: "Assisted Living & Memory Care",
    body:
      "Every ALF, memory-care community, and independent-living residence — Buena Vida, The Fountains, Grand Villa, Zon Beachside, and dozens more.",
  },
  {
    icon: MapPin,
    title: "Private Residences",
    body:
      "In your parent's living room, at the kitchen table, or in a caregiver's home — we come to where the signer is most comfortable and lucid.",
  },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Discreet Intake Call",
    body:
      "You call or email. We ask about the signer's condition, awareness, the document(s) involved, the venue, and any timing urgency. No forms to fill first — just a human conversation.",
  },
  {
    step: "02",
    title: "Facility Coordination",
    body:
      "If the visit is at a hospital, hospice, or ALF, we call the facility ahead, verify visitor policies, coordinate with the nursing station, and confirm your appointment window.",
  },
  {
    step: "03",
    title: "Bedside Execution",
    body:
      "Arrival with credentials, seal, journal, and appropriate witnesses. We verify identity per Florida statute, confirm signer awareness and willingness, and execute the notarization with full documentation.",
  },
  {
    step: "04",
    title: "Follow-Through & Records",
    body:
      "Signed originals returned to family or attorney per your instruction. Journal entry sealed. If apostille is needed for out-of-state or international use, we can carry the document through Tallahassee.",
  },
];

export default function ElderCarePage() {
  return (
    <>
      {/* Elder Care structured data — dedicated Service JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(ELDER_CARE_JSONLD) }}
      />

      <PageHero
        eyebrow="Pillar 5 · Our Signature Focus"
        title="Elder Care & Senior Specialist Notary Services."
        lead="Compassionate, dignified, and Chapter 117-compliant notary service for seniors, hospice patients, assisted-living residents, and their families. Delivered by a Florida-commissioned notary who is also a Certified Senior Advisor (CSA) Candidate and End-of-Life (EoL) Doula Candidate — because dignity, discipline, and empathy don't happen by accident."
        backdrop={IMG.eldercareHero}
        backdropOpacity={0.55}
      />

      {/* Trust chips */}
      <section className="py-16 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <TrustChip icon={ShieldCheck} label="Bedside · Hospice · ALF" />
            <TrustChip icon={Award} label="CSA Candidate" />
            <TrustChip icon={HeartHandshake} label="EoL Doula Candidate" />
            <TrustChip icon={BadgeCheck} label="Fla. Chapter 117 Compliant" />
          </div>
        </div>
      </section>

      {/* Signature focus — CSA + EoL credentials block */}
      <section className="py-24 sm:py-32">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <SectionEyebrow className="mb-4">Our Approach</SectionEyebrow>
              <h2 className="font-serif-display text-4xl sm:text-5xl text-white leading-tight tracking-tight">
                Precision at the bedside. Empathy in the room. Discipline on the record.
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-6 text-slate-300 leading-relaxed text-base">
              <p>
                Elder-care notarization is not a transaction — it is a moment.
                A daughter watching her father sign a healthcare surrogate two
                days into ICU. A son racing a durable power of attorney to
                Cape Canaveral Hospital before an emergency surgery. A spouse
                finalizing a revocable living trust from a hospice bed. These
                are the moments where Florida statute meets human dignity, and
                the notary in the room sets the tone.
              </p>
              <p>
                Dr. Gregory A. Byers approaches every elder-care assignment
                with 45 years of federal-grade operational discipline paired
                with the deliberate softness of specialized training as a{" "}
                <span className="text-[#D4AF37]">
                  Certified Senior Advisor (CSA) Candidate
                </span>{" "}
                and{" "}
                <span className="text-[#D4AF37]">
                  End-of-Life (EoL) Doula Candidate
                </span>
                . Facility protocols are respected. Nursing staff are
                coordinated with, not around. Signer capacity is verified with
                unhurried care.
              </p>
              <p>
                Every document is executed with the same rigor as a DoD
                packet, and every visit is conducted with the empathy of a
                family friend. That is the OGO Standard for our signature
                pillar.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Documents we handle */}
      <section className="relative py-24 sm:py-32 border-y border-white/10">
        <FadedBackdrop src={IMG.eldercareHero} opacity={0.18} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-14">
            <SectionEyebrow className="mb-4">Documents We Handle</SectionEyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-white leading-tight tracking-tight">
              The full arc of senior legal documentation, notarized in place.
            </h2>
            <p className="mt-6 text-slate-300 leading-relaxed">
              We coordinate closely with elder-law attorneys, estate planners,
              hospice social workers, and family caregivers. OGO Notary does
              not draft documents — please have them prepared by your
              attorney or via a reputable Florida-compliant template before
              our visit.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {DOCUMENT_TYPES.map((d) => (
              <DocCard key={d.title} {...d} />
            ))}
          </div>
        </div>
      </section>

      {/* Where we serve */}
      <section className="py-24 sm:py-32 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-14">
            <SectionEyebrow className="mb-4">Where We Serve</SectionEyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-white leading-tight tracking-tight">
              We come to where your loved one is — every day, every venue.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {VENUES.map((v) => (
              <VenueCard key={v.title} {...v} />
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 sm:py-32 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-14">
            <SectionEyebrow className="mb-4">How the Visit Works</SectionEyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-white leading-tight tracking-tight">
              Four steps. Zero surprises. Total dignity.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {HOW_IT_WORKS.map((s) => (
              <StepCard key={s.step} {...s} />
            ))}
          </div>
        </div>
      </section>

      {/* Urgency / same-day banner */}
      <section className="py-20 sm:py-24 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="border border-[#D4AF37]/40 bg-gradient-to-br from-[#1A2B45] via-[#1A2B45] to-[#0F1B2E] p-8 sm:p-10 lg:p-12">
            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-2 flex lg:justify-center">
                <div className="w-14 h-14 rounded-full border-2 border-[#D4AF37] flex items-center justify-center text-[#D4AF37]">
                  <Clock className="w-7 h-7" />
                </div>
              </div>
              <div className="lg:col-span-7">
                <SectionEyebrow className="mb-2 text-[#D4AF37]">When Time Matters</SectionEyebrow>
                <h3 className="font-serif-display text-3xl text-white leading-tight">
                  Same-day, evening, and weekend elder-care visits available.
                </h3>
                <p className="mt-3 text-slate-300 leading-relaxed">
                  Health transitions rarely happen on business hours. If your
                  loved one&apos;s condition is changing today, call directly
                  — we will move mountains to get to the bedside.
                </p>
              </div>
              <div className="lg:col-span-3 space-y-3">
                <a
                  href="tel:+18555054722"
                  data-testid="elder-care-call-cta"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#142339] px-5 py-4 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[#FDE047] transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  (855) 505-4722
                </a>
                <a
                  href="mailto:Greg@ogonotary.com?subject=Urgent%20Elder-Care%20Notarization%20Request"
                  data-testid="elder-care-email-cta"
                  className="w-full inline-flex items-center justify-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] px-5 py-3 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors"
                >
                  <Mail className="w-3.5 h-3.5" />
                  Email Greg
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 sm:py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <SectionEyebrow className="mb-6 justify-center">Ready to Book</SectionEyebrow>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-white leading-tight tracking-tight">
            Give your family one less thing to worry about.
          </h2>
          <p className="mt-6 text-slate-300 leading-relaxed text-lg">
            Tell us where your loved one is, what documents need notarization,
            and when you need us. We&apos;ll take it from there.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <GoldButton
              as={Link}
              to="/contact?service=Elder+Care+Notary"
              data-testid="elder-care-book-cta"
            >
              Book an Elder-Care Visit <ArrowUpRight className="w-4 h-4" />
            </GoldButton>
            <GhostGoldButton
              as={Link}
              to="/faq"
              data-testid="elder-care-faq-cta"
            >
              Read Elder-Care FAQ
            </GhostGoldButton>
          </div>
          <p className="mt-8 text-[11px] uppercase tracking-[0.22em] text-slate-500 flex items-center justify-center gap-2">
            <Calendar className="w-3 h-3" />
            Bedside · Hospice · ALF · Home · 7 days a week by appointment
          </p>
        </div>
      </section>
    </>
  );
}

function DocCard({ icon: Icon, title, body }) {
  return (
    <div
      data-testid={`elder-care-doc-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
      className="border border-white/10 bg-[#1A2B45]/80 backdrop-blur p-7 hover:border-[#D4AF37]/40 transition-colors"
    >
      <div className="w-11 h-11 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] mb-5">
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="font-serif-display text-xl text-white mb-3 leading-tight">
        {title}
      </h3>
      <p className="text-slate-300 text-sm leading-relaxed">{body}</p>
    </div>
  );
}

function VenueCard({ icon: Icon, title, body }) {
  return (
    <div
      data-testid={`elder-care-venue-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
      className="border border-white/10 bg-[#1A2B45] p-8 hover:border-[#D4AF37]/40 transition-colors"
    >
      <div className="flex items-start gap-5">
        <div className="w-12 h-12 shrink-0 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37]">
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <h3 className="font-serif-display text-2xl text-white mb-3 leading-tight">
            {title}
          </h3>
          <p className="text-slate-300 leading-relaxed">{body}</p>
        </div>
      </div>
    </div>
  );
}

function StepCard({ step, title, body }) {
  return (
    <div className="border-l-2 border-[#D4AF37] pl-6 py-2">
      <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-semibold mb-3">
        Step {step}
      </div>
      <h3 className="font-serif-display text-xl text-white mb-3 leading-tight">
        {title}
      </h3>
      <p className="text-slate-300 text-sm leading-relaxed">{body}</p>
    </div>
  );
}
